import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Loader2, Search, Download, Coins, ShieldCheck, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { apiService } from '../services/api.service';
import { toast } from 'sonner';

interface License {
  id: string;
  event_id: string | null;
  event_name: string | null;
  organization_id: string;
  org_name: string;
  org_token_balance: number;
  product_name: string;
  product_slug: string;
  token_cost: number;
  status: string;
  source: string;
  max_lanes: number;
  max_days: number;
  tokens_consumed: number;
  final_price_cents: number;
  discount_applied_cents: number;
  activated_at: string | null;
  created_at: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  token_balance: number;
  welcome_tokens_granted: boolean;
  stripe_connect_active: boolean;
  license_count: number;
  total_tokens_purchased: number;
  total_tokens_consumed: number;
  events_count: number;
  members_count: number;
}

interface Product {
  id: string;
  name: string;
  slug: string;
  token_cost: number;
  base_price_cents: number;
}

const AdminLicenses: React.FC = () => {
  const { t } = useTranslation();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterTier, setFilterTier] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [giftDialogOpen, setGiftDialogOpen] = useState(false);
  const [giftForm, setGiftForm] = useState({
    organization_id: '',
    amount: '',
    product_id: '',
    reason: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [licensesRes, productsRes] = await Promise.all([
        apiService.getAdminLicenses(),
        apiService.getLicenseProducts(),
      ]);
      setLicenses(licensesRes.licenses || []);
      setOrgs(licensesRes.organizations || []);
      setProducts(productsRes.products || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleGift = async () => {
    try {
      const data: any = {
        organization_id: giftForm.organization_id,
        reason: giftForm.reason || undefined,
      };

      if (giftForm.amount) {
        data.amount = parseInt(giftForm.amount);
      } else if (giftForm.product_id) {
        data.product_id = giftForm.product_id;
      } else {
        toast.error('Spécifier un nombre de jetons ou un tier');
        return;
      }

      const res = await apiService.giftTokens(data);
      toast.success(res.message || 'Jetons offerts');
      setGiftDialogOpen(false);
      setGiftForm({ organization_id: '', amount: '', product_id: '', reason: '' });
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Erreur gift jetons');
    }
  };

  const filteredOrgs = orgs.filter(o => {
    if (!searchQuery) return true;
    return o.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredLicenses = licenses.filter(lic => {
    if (filterStatus !== 'all' && lic.status !== filterStatus) return false;
    if (filterTier !== 'all' && lic.product_slug !== filterTier) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        lic.org_name?.toLowerCase().includes(q) ||
        lic.event_name?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const exportCSV = () => {
    const headers = ['Organisation', 'Solde', 'Event', 'Tier', 'Tokens', 'Source', 'Status', 'Date'];
    const rows = filteredLicenses.map(l => [
      l.org_name,
      l.org_token_balance,
      l.event_name || '',
      l.product_name,
      l.tokens_consumed,
      l.source,
      l.status,
      l.activated_at ? new Date(l.activated_at).toLocaleDateString() : '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'licenses.csv';
    a.click();
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-success/20 text-success';
      case 'expired': return 'bg-destructive/20 text-destructive';
      case 'unassigned': return 'bg-white/[0.06] text-muted-foreground';
      default: return 'bg-slate-500/20 text-slate-300';
    }
  };

  const sourceLabel = (source: string) => {
    switch (source) {
      case 'token': return t('admin.licenses.sourceToken');
      case 'admin_bypass': return 'Admin';
      case 'gift': return t('admin.licenses.sourceGift');
      case 'stripe': return 'Stripe';
      case 'free_trial': return t('admin.licenses.sourceTrial');
      default: return source;
    }
  };

  // Summary stats
  const totalTokensInCirculation = orgs.reduce((sum, o) => sum + o.token_balance, 0);
  const orgsWithTokens = orgs.filter(o => o.token_balance > 0).length;
  const orgsWithConnect = orgs.filter(o => o.stripe_connect_active).length;

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl md:text-3xl font-bold">{t('admin.licenses.title')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV}>
            <Download size={16} className="mr-1" />
            CSV
          </Button>
          <Button size="sm" onClick={() => setGiftDialogOpen(true)}>
            <Gift size={16} className="mr-1" />
            {t('admin.licenses.giftTokens')}
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        <Card>
          <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
            <Coins size={18} className="text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <div className="text-xl md:text-2xl font-bold">{totalTokensInCirculation}</div>
              <div className="text-[10px] md:text-xs text-muted-foreground truncate">{t('admin.licenses.totalTokens')}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
            <Building2 size={18} className="text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <div className="text-xl md:text-2xl font-bold">{orgsWithTokens}/{orgs.length}</div>
              <div className="text-[10px] md:text-xs text-muted-foreground truncate">{t('admin.licenses.orgsWithTokens')}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
            <ShieldCheck size={18} className="text-success shrink-0" />
            <div className="min-w-0">
              <div className="text-xl md:text-2xl font-bold">{orgsWithConnect}</div>
              <div className="text-[10px] md:text-xs text-muted-foreground truncate">Stripe Connect</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t('admin.licenses.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={32} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Organizations Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">
                {t('admin.licenses.organizations')} ({filteredOrgs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.licenses.organization')}</TableHead>
                    <TableHead className="text-center">
                      <Coins size={14} className="inline mr-1" />
                      {t('admin.licenses.balance')}
                    </TableHead>
                    <TableHead className="text-center">{t('admin.licenses.purchased')}</TableHead>
                    <TableHead className="text-center">{t('admin.licenses.consumed')}</TableHead>
                    <TableHead className="text-center">{t('admin.licenses.events')}</TableHead>
                    <TableHead className="text-center">{t('admin.licenses.members')}</TableHead>
                    <TableHead className="text-center">{t('admin.licenses.eventLicenses')}</TableHead>
                    <TableHead className="text-center">Stripe Connect</TableHead>
                    <TableHead className="text-center">{t('admin.licenses.welcome')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrgs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-6">
                        {t('admin.licenses.noOrganizations')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrgs.map((org) => (
                      <TableRow key={org.id}>
                        <TableCell className="font-medium">{org.name}</TableCell>
                        <TableCell className="text-center">
                          <Badge
                            variant="outline"
                            className={org.token_balance > 0 ? 'border-primary text-foreground' : ''}
                          >
                            {org.token_balance}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground text-sm">
                          {org.total_tokens_purchased > 0 ? `+${org.total_tokens_purchased}` : '—'}
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground text-sm">
                          {org.total_tokens_consumed > 0 ? `-${org.total_tokens_consumed}` : '—'}
                        </TableCell>
                        <TableCell className={`text-center text-sm ${org.events_count === 0 ? 'text-warning' : 'text-muted-foreground'}`}>
                          {org.events_count}
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground text-sm">
                          {org.members_count}
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground text-sm">
                          {org.license_count > 0 ? org.license_count : '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          {org.stripe_connect_active ? (
                            <ShieldCheck size={16} className="inline text-success" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {org.welcome_tokens_granted ? (
                            <Badge className="bg-white/[0.06] text-foreground text-xs">10</Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">{t('admin.licenses.pending')}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Event Licenses Table */}
          {licenses.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <CardTitle className="text-lg">{t('admin.licenses.eventLicenses')} ({filteredLicenses.length})</CardTitle>
                  <div className="flex gap-2 sm:ml-auto">
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-28 sm:w-36 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('admin.licenses.allStatuses')}</SelectItem>
                        <SelectItem value="active">{t('admin.licenses.statusActive')}</SelectItem>
                        <SelectItem value="expired">{t('admin.licenses.statusExpired')}</SelectItem>
                        <SelectItem value="unassigned">{t('admin.licenses.statusUnassigned')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterTier} onValueChange={setFilterTier}>
                      <SelectTrigger className="w-28 sm:w-36 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('admin.licenses.allTiers')}</SelectItem>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="competition">Competition</SelectItem>
                        <SelectItem value="major">Major</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin.licenses.organization')}</TableHead>
                      <TableHead>{t('admin.licenses.event')}</TableHead>
                      <TableHead>{t('admin.licenses.tier')}</TableHead>
                      <TableHead className="text-center">{t('admin.licenses.tokensUsed')}</TableHead>
                      <TableHead>{t('admin.licenses.source')}</TableHead>
                      <TableHead>{t('admin.licenses.status')}</TableHead>
                      <TableHead>{t('admin.licenses.activatedAt')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLicenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                          {t('admin.licenses.noLicenses')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLicenses.map((lic) => (
                        <TableRow key={lic.id}>
                          <TableCell className="font-medium">{lic.org_name}</TableCell>
                          <TableCell>{lic.event_name || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{lic.product_name}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {lic.tokens_consumed > 0 ? lic.tokens_consumed : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {sourceLabel(lic.source)}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColor(lic.status)}>{lic.status}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {lic.activated_at ? new Date(lic.activated_at).toLocaleDateString() : '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Gift Tokens Dialog */}
      <Dialog open={giftDialogOpen} onOpenChange={setGiftDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('admin.licenses.giftTokensTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('admin.licenses.giftTokensDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('admin.licenses.organization')}</Label>
              <Select
                value={giftForm.organization_id}
                onValueChange={(v) => setGiftForm({ ...giftForm, organization_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.licenses.selectOrganization')} />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name} · {t('admin.licenses.eventsCount', { count: org.events_count })} · {t('admin.licenses.membersCount', { count: org.members_count })} · {org.token_balance} {t('admin.licenses.tokensUnit')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const selected = orgs.find((o) => o.id === giftForm.organization_id);
                if (!selected || selected.events_count > 0) return null;
                return (
                  <p className="mt-2 text-sm text-warning" role="alert">
                    {t('admin.licenses.noEventsWarning', { members: selected.members_count })}
                  </p>
                );
              })()}
            </div>
            <div>
              <Label>{t('admin.licenses.tokenAmount')}</Label>
              <NumberInput
                value={giftForm.amount}
                onChange={(e) => setGiftForm({ ...giftForm, amount: e.target.value, product_id: '' })}
                placeholder="Nombre de jetons (ex: 25)"
              />
              <p className="text-xs text-muted-foreground mt-1">{t('admin.licenses.orSelectTier')}</p>
              <Select
                value={giftForm.product_id}
                onValueChange={(v) => setGiftForm({ ...giftForm, product_id: v, amount: '' })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('admin.licenses.selectTier')} />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => p.slug && !p.slug.startsWith('pack-')).map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.token_cost} jetons)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('admin.licenses.reason')}</Label>
              <Input
                value={giftForm.reason}
                onChange={(e) => setGiftForm({ ...giftForm, reason: e.target.value })}
                placeholder={t('admin.licenses.reasonPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGiftDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleGift}
              disabled={!giftForm.organization_id || (!giftForm.amount && !giftForm.product_id)}
            >
              <Gift size={18} className="mr-2" />
              {t('admin.licenses.giftConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLicenses;
